import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  IconButton,
  TablePagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Grid,
  Toolbar,
  InputAdornment,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import Sidebar from "../../Components/Sidebar";
import EditIcon from "@mui/icons-material/Edit";
import { Select } from "@mui/material";
import {  Menu } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

const AdminUserManagement = () => {
  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Users data
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
const [error, setError] = useState("");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Search
  const [searchQuery, setSearchQuery] = useState("");

  // Add User modal
  const [openAddUser, setOpenAddUser] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [forcePasswordChange, setForcePasswordChange] = useState(false);
  const [role, setRole] = useState("");
const [editingUserId, setEditingUserId] = useState(null);
const [roleFilter, setRoleFilter] = useState("");
const backendURL = process.env.REACT_APP_TWCHATBOT_DOMAINS;

const [anchorEl, setAnchorEl] = useState(null);
const open = Boolean(anchorEl);


 
const [emailError, setEmailError] = useState(""); // <-- new state to track validation message

  // Mock fetch users
  useEffect(() => {
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${backendURL}/internal/get_users/`);
      if (!response.ok) throw new Error("Failed to fetch users");
      const data = await response.json();
      console.log("USERS LIST FROM BACKEND",data)
      // assuming backend returns a list of users like:
      // [{user_id, first_name, last_name, email, role}]
      setUsers(
        data.map(u => ({
           id: u.id,
          firstName: u.firstName,
          lastName: u.lastName,
          email: u.email,
          role: u.role
        }))
      );
    } catch (err) {
      console.error(err);
      alert("❌ Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };
  fetchUsers();
}, []);


const handleSaveUser = async () => {
  try {
    const newUser = {
      first_name: firstName,
      last_name: lastName,
      email: email,
      role: role,
      force_password_change: forcePasswordChange,
    };
    console.log("DATA SENDING TO BACKEND", newUser);

    // 👉 Check if we are editing
    if (editingUserId) {
      // ✅ UPDATE existing user
      const response = await fetch(`${backendURL}/internal/update_user/${editingUserId}/`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(newUser),
        }
      );

      const data = await response.json(); // always parse once
      if (!response.ok) {
        // backend should send { message: "..." } or similar
        throw new Error(data.message || data.error || "Something went wrong");
      }

      // Update local state
      setUsers((prev) =>
        prev.map((u) =>
          u.id === editingUserId
            ? {
                ...u,
                firstName,
                lastName,
                email,
                role,
              }
            : u
        )
      );

      setEditingUserId(null);
      alert("✅ User updated!");
    } else {
      // ✅ CREATE new user
      const response = await fetch(`${backendURL}/internal/add_user/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newUser),
      });

      const data = await response.json(); // always parse first
      console.log("RESPONSE FROM BACKEND", data);

      if (!response.ok) {
        // show backend error to user
        throw new Error(data.message || data.error || "Something went wrong");
      }

      // Success
      alert(
        `✅ User created!\nUserID: ${data.user_id}\nDefault Password: ${data.default_password}`
      );

      // Add new user to local state
      setUsers((prev) => [
        {
          id: data.user_id,
          firstName,
          lastName,
          email,
          role,
        },
        ...prev,
      ]);
    }

    // ✅ Reset form fields & close dialog
    setOpenAddUser(false);
    setFirstName("");
    setLastName("");
    setEmail("");
    setForcePasswordChange(false);
    setRole("User");
    setError(""); // clear any previous error
  } catch (err) {
    console.error("❌ Error in handleSaveUser:", err);
    // show the error message (from backend or generic)
    alert(`❌ ${err.message}`);
    // if you have error state, update it:
    // const [error, setError] = useState("");
    setError(err.message);
  }
};


const handleEmailChange = (e) => {
  const value = e.target.value;
  setEmail(value);

  // Live validation
  if (value && !value.toLowerCase().endsWith("@techwave.com")) {
    setEmailError("Email must end with @techwave.com");
  } else {
    setEmailError("");
  }
};

const handleEditUser = (user) => {
  // prefill dialog fields
  setFirstName(user.firstName);
  setLastName(user.lastName);
  setEmail(user.email);
  setRole(user.role);
  setForcePasswordChange(false);
  setEditingUserId(user.id); // add this state to know if editing
  setOpenAddUser(true);
};


 const handleDeleteUser = async (id) => {
  if (window.confirm("Are you sure you want to delete this user?")) {
    try {
      const response = await fetch(`${backendURL}/internal/delete_user/${id}/`,
        { method: "DELETE" }
      );

      if (!response.ok) {
        throw new Error("Failed to delete user");
      }

      // update frontend state
      setUsers((prev) => prev.filter((u) => u.id !== id));
      alert("✅ User deleted successfully!");
    } catch (err) {
      console.error(err);
      alert("❌ Failed to delete user. Please try again.");
    }
  }
};


const filteredUsers = users.filter((user) => {
  const idString = (user.id || "").toString().toLowerCase();

  const matchesSearch =
    (user.firstName?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
    (user.lastName?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
    (user.email?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
    idString.includes(searchQuery.toLowerCase());

  const matchesRole = roleFilter ? user.role === roleFilter : true;

  return matchesSearch && matchesRole;
});


 const paginatedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

const handleMenuOpen = (event) => {
  setAnchorEl(event.currentTarget);
};

const handleMenuClose = () => {
  setAnchorEl(null);
};

const handleRoleFilter = (value) => {
  setRoleFilter(value); // your filter state
  handleMenuClose();
};


  return (
  <Grid
  container
  direction="column"
  sx={{
    backgroundColor: "#f8f9fb",
    height: "100%",
    overflow: "hidden",
   
  }}
>
      {/* Navbar toggle */}
      {!sidebarOpen && (
        <Grid item xs={12}>
          <IconButton
            onClick={() => setSidebarOpen(true)}
            sx={{
              position: "fixed",
              marginTop: "64px",
              left: "16px",
              zIndex: 1300,
            }}
          >
            <MenuIcon style={{ color: "black" }} />
          </IconButton>
        </Grid>
      )}

      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        currentPage="admin-users"
        onClose={() => setSidebarOpen(false)}
        sidebarOpen={sidebarOpen}
        isAdminAuthenticated={true}
        sx={{
          width: sidebarOpen ? "20%" : "0",
          transition: "width 0.3s ease",
          "@media (max-width: 600px)": {
            width: sidebarOpen ? "100%" : "0",
          },
        }}
      />

      {/* Main content */}
     <Grid
  item
  xs
  style={{
    display: "flex",
    flexDirection: "column",
    marginLeft: sidebarOpen ? "24.5%" : "0",
    width: sidebarOpen ? "75%" : "100%",
    transition: "all 0.3s ease",
    paddingTop: "100px", // 👈 pushes content below navbar & hamburger
    paddingLeft: "24px",
    paddingRight: "24px",
    boxSizing: "border-box",
   
  }}
>

  <Box sx={{ paddingBottom: 3 }}>

          <Paper
            sx={{
              padding: 3,
              borderRadius: "16px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
               minHeight: "559px", // Adjust based on desired look
    display: "flex",
    flexDirection: "column",
            }}
          >
           <Box
  sx={{
    display: "flex",
    alignItems: "center", // vertically center icon & text
    mb: 1.5,
    
  }}
>
  <Typography
    component="span"
    sx={{
      fontSize: "2rem", // adjust icon size
      mr: 1, 
      mb:1           // spacing between icon and text
    }}
  >
    👥
  </Typography>
  <Typography
    variant="h4"
    sx={{
      fontFamily: "Kumbh Sans",
      fontWeight: 700,
      color: "#153052",
    }}
  >
    User Management
  </Typography>
</Box>

            {/* Toolbar with search & add button */}
            <Toolbar
              sx={{
                display: "flex",
                justifyContent: "space-between",
                paddingX: 0,
                mb: 2,
              }}
            >
             <TextField
  placeholder="Search by name or email..."
  value={searchQuery}
  onChange={(e) => setSearchQuery(e.target.value)}
  size="small"
  sx={{
    width: "30%",
    backgroundColor: "#fff",
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px", 
      "& fieldset": {
        borderRadius: "8px",
      },
    },
  }}
  InputProps={{
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon color="action" />
      </InputAdornment>
    ),
  }}
/>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenAddUser(true)}
                sx={{
                  backgroundColor: "#153052",
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: "#10263f",
                  },
                }}
              >
                Add User
              </Button>
            </Toolbar>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer
  sx={{
    borderRadius: "12px",
    border: "1px solid #e0e0e0",
         // ✅ no scrollbar unless needed
    maxHeight: "unset",  
    //  overflow: "auto",
    //   flexGrow: 1,         // fill remaining space
    //   minHeight: "400px",  // keep consistent height
  }}
>
  <Table size="small">
    {/* ===== Table Head ===== */}
    <TableHead>
      <TableRow
        sx={{
          backgroundColor: "#153052",
          height: 48, // taller header row
          '& > *': {
            py: 1.5, // more vertical padding for header cells
            color: "#fff",
            fontWeight: "bold",
            fontSize: "0.95rem",
          },
        }}
      >
        <TableCell sx={{ color: "#153052" }}>ID</TableCell>
        <TableCell sx={{ color: "#153052" }}>Name</TableCell>
        <TableCell sx={{ color: "#153052" }}>Email</TableCell>
        <TableCell sx={{ color: "#153052" }}>
          Role
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{ color: "#153052", ml: 0.5 }}
          >
            <ArrowDropDownIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            MenuListProps={{ dense: true }}
          >
            <MenuItem onClick={() => handleRoleFilter("")}>All</MenuItem>
            <MenuItem onClick={() => handleRoleFilter("Admin")}>Admin</MenuItem>
            <MenuItem onClick={() => handleRoleFilter("User")}>User</MenuItem>
          </Menu>
        </TableCell>
        <TableCell sx={{ color: "#153052" }}>Actions</TableCell>
      </TableRow>
    </TableHead>

    {/* ===== Table Body ===== */}
    <TableBody>
      {paginatedUsers.map((user) => (
        <TableRow
          key={user.id}
          hover
          sx={{
            height: 32, // smaller height for body rows
            '& > *': {
              py: 0.5, // less padding for body cells
            },
            '&:hover': {
              backgroundColor: "#f1f5f9",
            },
          }}
        >
          <TableCell>{user.id}</TableCell>
          <TableCell>{user.firstName} {user.lastName}</TableCell>
          <TableCell>{user.email}</TableCell>
          <TableCell>{user.role}</TableCell>
          <TableCell>
            <IconButton color="primary" onClick={() => handleEditUser(user)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton color="error" onClick={() => handleDeleteUser(user.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
</TableContainer>


                <TablePagination
                  component="div"
                  count={filteredUsers.length}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  sx={{ mt: 1 }}
                />
              </>
            )}
          </Paper>
        </Box>
      </Grid>

      {/* Add User Dialog */}
      <Dialog
        open={openAddUser}
        onClose={() => setOpenAddUser(false)}
        PaperProps={{ sx: { borderRadius: "12px", width: "500px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#153052" }}>Add New User</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth margin="dense" label="First Name"
            value={firstName} onChange={(e) => setFirstName(e.target.value)}
          />
          <TextField
            fullWidth margin="dense" label="Last Name"
            value={lastName} onChange={(e) => setLastName(e.target.value)}
          />
          <TextField
                fullWidth
                margin="dense"
                type="email"
                label="Email Address"
                value={email}
                onChange={handleEmailChange}
                error={!!emailError} // show error styling
                helperText={emailError} // show error message
                />

          
          <TextField
            select fullWidth margin="dense" label="Role"
            value={role} onChange={(e) => setRole(e.target.value)}
          >
            <MenuItem value="" disabled>
    Select Role
  </MenuItem>
            <MenuItem value="Admin">Admin</MenuItem>
            <MenuItem value="User">User</MenuItem>
          </TextField>
          <FormControlLabel
            control={
              <Checkbox
                checked={forcePasswordChange}
                onChange={(e) => setForcePasswordChange(e.target.checked)}
              />
            }
            label="Force password change on first login"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
  <Button onClick={() => setOpenAddUser(false)}>Cancel</Button>
  <Button
    variant="contained"
    sx={{ backgroundColor: "#153052", textTransform: "none", fontWeight: 600 }}
    onClick={handleSaveUser}
    disabled={
      !firstName.trim() ||
      !lastName.trim() ||
      !email.trim() ||
      !role.trim() ||
      !!emailError   // disable if email error exists
    }
  >
   {editingUserId ? "Update User" : "Save User"}
  </Button>
</DialogActions>

      </Dialog>
    </Grid>
  );
};

export default AdminUserManagement;
